import client from './client';
import { fetchAllPages, toRawListParams } from './paging';
import { Project, PaginatedResponse, ProjectListParams } from '../types/index';

// Bug #328 / WI #323: the backend serialises snake_case and nothing converts
// it — `api/client.ts`'s response interceptor is the identity function. This
// module previously typed the raw payload as the camelCase `Project`, which
// TypeScript accepted happily and which made every non-identical field
// (`source_system`, `created_at`, `client_id`, ...) resolve to `undefined` at
// runtime. Only `name` survived, because it is spelled the same on both
// sides — which is exactly what the projects list showed.
//
// Mapping once here follows the `api/users.ts` pattern (state 1 in the API
// case-convention note): camelCase TS types, one explicit mapper, every
// consumer downstream gets a consistent shape.
interface RawProject {
  id: string;
  name: string;
  description?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  status?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  source_url?: string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
}

const mapProject = (p: RawProject): Project => ({
  id: p.id,
  name: p.name,
  description: p.description ?? undefined,
  clientId: p.client_id ?? undefined,
  // Bug #330: the API now resolves the company name server-side. Still
  // optional — a deactivated or deleted company yields null rather than
  // failing the request.
  clientName: p.client_name ?? undefined,
  status: p.status ?? '',
  sourceSystem: p.source_system ?? undefined,
  sourceId: p.source_id ?? undefined,
  sourceUrl: p.source_url ?? undefined,
  isActive: Boolean(p.is_active),
  createdAt: p.created_at ?? '',
  updatedAt: p.updated_at ?? '',
  createdBy: p.created_by ?? '',
});

interface RawPaginatedProjects {
  items: RawProject[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Requests go the other way: the backend binds snake_case bodies, so anything
// we send has to be snake_case too.
const toRawProject = (data: Partial<Project>): Record<string, unknown> => {
  const raw: Record<string, unknown> = {};
  if (data.name !== undefined) raw.name = data.name;
  if (data.description !== undefined) raw.description = data.description;
  if (data.clientId !== undefined) raw.client_id = data.clientId;
  // Bug #334: statuses are stored lowercase ('active', 'completed',
  // 'archived') and `GET /projects` filters on an exact match
  // (`LabProject.status == status`). The Add and Edit forms both offer
  // capitalised options, so writing the label verbatim would store 'Active'
  // and make the project invisible under the list's Active filter — which
  // lowercases its side of the comparison. Normalised here rather than in
  // each form: this is the single write path for both create and update.
  if (data.status !== undefined) raw.status = data.status.toLowerCase();
  if (data.sourceSystem !== undefined) raw.source_system = data.sourceSystem;
  if (data.sourceId !== undefined) raw.source_id = data.sourceId;
  if (data.sourceUrl !== undefined) raw.source_url = data.sourceUrl;
  return raw;
};

const toRawProjectParams = (
  params?: ProjectListParams,
): Record<string, unknown> | undefined => {
  if (!params) return undefined;
  const raw: Record<string, unknown> = toRawListParams(params) || {};
  if (params.clientId !== undefined) raw.client_id = params.clientId;
  if (params.status !== undefined) raw.status = params.status;
  return raw;
};

export const getProjects = async (
  params?: ProjectListParams
): Promise<PaginatedResponse<Project>> => {
  // WI #337: see the note in `api/users.ts` — camelCase params were dropped.
  const response = await client.get<RawPaginatedProjects>('/projects', {
    params: toRawProjectParams(params),
  });
  const data = response.data;
  return {
    items: (data.items || []).map(mapProject),
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

// Bug #331: the "Associated projects" tab on a company detail page. The
// backend already exposes `client_id` as a filter on GET /projects, so this
// needs no new endpoint — but it does need the param spelled snake_case,
// which is why it is an explicit function rather than a `ListParams` field
// passed through `getProjects` (that path still forwards camelCase keys and
// is tracked on WI #323).
export const getProjectsForCompany = async (companyId: string): Promise<Project[]> => {
  // WI #337: was capped at one 100-row page. A client with more projects than
  // that silently lost the tail, so this now walks every page.
  const rows = await fetchAllPages<RawProject>('/projects', { client_id: companyId });
  return rows.map(mapProject);
};

export const getProject = async (id: string): Promise<Project> => {
  const response = await client.get<RawProject>(`/projects/${id}`);
  return mapProject(response.data);
};

export const createProject = async (data: Partial<Project>): Promise<Project> => {
  const response = await client.post<RawProject>('/projects', toRawProject(data));
  return mapProject(response.data);
};

export const updateProject = async (id: string, data: Partial<Project>): Promise<Project> => {
  // PATCH, not PUT — the backend only exposes PATCH /projects/{id}.
  const response = await client.patch<RawProject>(`/projects/${id}`, toRawProject(data));
  return mapProject(response.data);
};

// Deactivation is a soft-delete on the backend: DELETE flips is_active=false
// and records an audit entry. The previous PATCH of `{isActive: false}` could
// never have worked — the field is camelCase and `ProjectUpdate` does not
// accept `is_active` at all, so the request was silently a no-op. Same shape
// and same reasoning as `deactivateUser` (Bug #143).
export const deactivateProject = async (id: string): Promise<void> => {
  await client.delete(`/projects/${id}`);
};
